"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PayrollItemType = void 0;
exports.getPayrollReport = getPayrollReport;
exports.getHourlyRate = getHourlyRate;
exports.getAncillaryCostFactor = getAncillaryCostFactor;
exports.getProratedPaymentForRange = getProratedPaymentForRange;
exports.calculateProratedPayment = calculateProratedPayment;
exports.transformToPayrollIntervals = transformToPayrollIntervals;
exports.getPayrollPeriod = getPayrollPeriod;
const model_1 = require("./model");
const openapi_1 = require("@pentacode/openapi");
const utils_1 = require("./utils");
const localized_1 = require("./localized");
const contract_util_1 = require("./contract.util");
var PayrollItemType;
(function (PayrollItemType) {
    PayrollItemType["Salary"] = "salary";
    PayrollItemType["TimeWage"] = "time_wage";
    PayrollItemType["Commission"] = "commission";
    PayrollItemType["Absence"] = "absence";
    PayrollItemType["BonusTaxFree"] = "bonus_tax_free";
    PayrollItemType["BonusTaxed"] = "bonus_taxed";
    PayrollItemType["BonusAdvance"] = "bonus_advance";
    PayrollItemType["Benefit"] = "benefit";
    PayrollItemType["WorkDays"] = "work_days";
    PayrollItemType["VacationDays"] = "vacation_days";
    PayrollItemType["SickDays"] = "sick_days";
    PayrollItemType["HourAdjustment"] = "hour_adjustment";
    PayrollItemType["VacationAdjustment"] = "vacation_adjustment";
    PayrollItemType["PayAdvance"] = "pay_advance";
    PayrollItemType["Meals"] = "meals";
})(PayrollItemType || (exports.PayrollItemType = PayrollItemType = {}));
function getPayrollReport(company, employee, timeEntries, advances, from, to) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const order = [
        PayrollItemType.TimeWage,
        PayrollItemType.Salary,
        PayrollItemType.Absence,
        PayrollItemType.Benefit,
        PayrollItemType.BonusTaxFree,
        PayrollItemType.BonusTaxed,
        PayrollItemType.BonusAdvance,
        PayrollItemType.Meals,
        PayrollItemType.Commission,
        PayrollItemType.HourAdjustment,
        PayrollItemType.VacationAdjustment,
        PayrollItemType.PayAdvance,
        // export only
        PayrollItemType.SickDays,
        PayrollItemType.VacationDays,
        PayrollItemType.WorkDays,
    ];
    const contracts = employee.getAllContractsForRange({ from, to }).filter((c) => !c.blocked);
    if (contracts.length === 0 || !contracts.some((contract) => contract.defaultSalary)) {
        return {
            employee,
            from,
            to,
            items: [],
            totalPay: 0,
        };
    }
    const items = [];
    function addItem(item) {
        const existing = items.find((i) => i.type === item.type &&
            i.wageType === item.wageType &&
            i.costCenter === item.costCenter &&
            i.unit === item.unit &&
            i.wagePerUnit === item.wagePerUnit &&
            i.factor === item.factor &&
            i.description === item.description &&
            i.exportOnly === item.exportOnly);
        if (existing) {
            existing.amount += item.amount;
            if (item.result) {
                existing.result = (existing.result || 0) + item.result;
            }
        }
        else {
            items.push(item);
        }
    }
    const date = { from, to };
    const defaultPosition = employee.positions[0];
    const defaultCostCenter = (_a = company.getCostCenter({ employee, date, position: defaultPosition })) === null || _a === void 0 ? void 0 : _a.number;
    const defaultWageTypeSet = company.getWageTypeSet({ employee, date, position: defaultPosition });
    // show prorated payments if multiple payroll periods or multiple contracts
    const startedPayrollPeriods = transformToPayrollIntervals(date, company.settings.startPayrollPeriod).length;
    const extendDescriptionWithPeriod = startedPayrollPeriods > 1 || contracts.length > 1;
    const formatIntervalForDescription = (interval) => {
        return `${(0, utils_1.formatDate)(interval.from)} - ${(0, utils_1.formatDate)((0, utils_1.dateAdd)(interval.to, { days: -1 }))}`;
    };
    const benefitItems = [];
    // Add payments
    for (const contract of contracts) {
        // monthly salaries
        for (const salary of contract.salaries.filter((s) => s.type === "monthly")) {
            for (const effectiveSalary of getProratedPaymentForRange(contract, date, salary, company.settings.startPayrollPeriod).filter((payment) => payment.amount !== 0)) {
                let description = `Gehalt ${company.getPositionLabel(salary.positionId || defaultPosition)}`;
                if (extendDescriptionWithPeriod) {
                    description += ` (${formatIntervalForDescription(effectiveSalary)})`;
                }
                addItem({
                    type: PayrollItemType.Salary,
                    description,
                    icon: "hands-holding-dollar",
                    unit: "euros",
                    amount: effectiveSalary.amount,
                    result: effectiveSalary.amount,
                    wageType: (_b = company.getWageTypeSet({ employee, position: salary.positionId || defaultPosition, date })) === null || _b === void 0 ? void 0 : _b.salary,
                    costCenter: (_c = company.getCostCenter({
                        employee,
                        position: salary.positionId || defaultPosition,
                        date,
                    })) === null || _c === void 0 ? void 0 : _c.number,
                });
            }
        }
        // SFN-Advance
        const sfnAdvance = contract.enableSFNLedger && contract.sfnAdvance;
        if (sfnAdvance) {
            for (const sfnAdvancePayment of getProratedPaymentForRange(contract, date, sfnAdvance, company.settings.startPayrollPeriod).filter((payment) => payment.amount !== 0)) {
                let description = "SFN-Pauschale";
                if (extendDescriptionWithPeriod) {
                    description += ` (${formatIntervalForDescription(sfnAdvancePayment)})`;
                }
                addItem({
                    type: PayrollItemType.BonusAdvance,
                    description,
                    icon: "badge-percent",
                    unit: "euros",
                    amount: sfnAdvancePayment.amount,
                    result: sfnAdvancePayment.amount,
                    wageType: defaultWageTypeSet === null || defaultWageTypeSet === void 0 ? void 0 : defaultWageTypeSet.bonusAdvance,
                });
            }
        }
        // benefits
        for (const benefit of contract.benefits) {
            const type = company.benefitTypes.find((b) => b.id === benefit.typeId);
            if (!type) {
                continue;
            }
            for (const benefitPayment of getProratedPaymentForRange(contract, date, benefit, company.settings.startPayrollPeriod).filter((payment) => payment.amount !== 0)) {
                let description = type.name;
                if (extendDescriptionWithPeriod) {
                    description += ` (${formatIntervalForDescription(benefitPayment)})`;
                }
                benefitItems.push({
                    amount: benefitPayment.amount,
                    result: benefitPayment.amount,
                    wageType: (_d = defaultWageTypeSet === null || defaultWageTypeSet === void 0 ? void 0 : defaultWageTypeSet.benefits.find((b) => b.benefitTypeId === benefit.typeId)) === null || _d === void 0 ? void 0 : _d.wageTypeNumber,
                    benefitType: benefit.typeId,
                    from: benefitPayment.from,
                    to: benefitPayment.to,
                    description,
                });
            }
        }
    }
    for (const item of benefitItems.sort((a, b) => `${a.benefitType}${a.from}`.localeCompare(`${b.benefitType}${b.from}`))) {
        addItem({
            type: PayrollItemType.Benefit,
            icon: "hand-holding-box",
            unit: "euros",
            amount: item.amount,
            result: item.result,
            wageType: item.wageType,
            costCenter: defaultCostCenter,
            description: item.description,
        });
    }
    for (const timeEntry of timeEntries) {
        const result = timeEntry.result;
        if (!result) {
            continue;
        }
        const position = timeEntry.position;
        const contract = employee.getContractForDate(timeEntry.date);
        const salary = (0, contract_util_1.getSalaryOrDefault)(position === null || position === void 0 ? void 0 : position.id, (contract === null || contract === void 0 ? void 0 : contract.salaries) || []);
        if (!contract || !salary) {
            continue;
        }
        const wageTypeSet = company.getWageTypeSet({ employee, position: position || defaultPosition, date });
        const absenceWageTypes = salary.type === "hourly" ? wageTypeSet === null || wageTypeSet === void 0 ? void 0 : wageTypeSet.absenceHourly : wageTypeSet === null || wageTypeSet === void 0 ? void 0 : wageTypeSet.absenceMonthly;
        const absenceDaysWageTypes = salary.type === "hourly" ? wageTypeSet === null || wageTypeSet === void 0 ? void 0 : wageTypeSet.absenceHourlyDays : wageTypeSet === null || wageTypeSet === void 0 ? void 0 : wageTypeSet.absenceMonthlyDays;
        const costCenter = (_e = company.getCostCenter({ employee, position: position || defaultPosition, date })) === null || _e === void 0 ? void 0 : _e.number;
        const paidDuration = (0, openapi_1.round)((0, openapi_1.add)(result.base.duration, result.breaks.paidDuration), 2);
        if (position) {
            const positionLabel = company.getPositionLabel(position) || "Unbekannte Position";
            // Time wage
            if ((salary === null || salary === void 0 ? void 0 : salary.type) === "hourly") {
                addItem({
                    type: PayrollItemType.TimeWage,
                    description: positionLabel,
                    icon: "timer",
                    unit: "hours",
                    amount: paidDuration,
                    wagePerUnit: salary.amount,
                    result: salary.amount * paidDuration,
                    wageType: wageTypeSet === null || wageTypeSet === void 0 ? void 0 : wageTypeSet.workHourly,
                    costCenter,
                });
            }
            else {
                addItem({
                    type: PayrollItemType.TimeWage,
                    description: positionLabel,
                    icon: "timer",
                    unit: "hours",
                    amount: paidDuration,
                    wageType: wageTypeSet === null || wageTypeSet === void 0 ? void 0 : wageTypeSet.workMonthly,
                    costCenter,
                });
            }
            // Work days
            if (result.days) {
                addItem({
                    type: PayrollItemType.WorkDays,
                    description: `${positionLabel} (Tage)`,
                    icon: "calendar-day",
                    unit: "days",
                    amount: result.days,
                    wageType: salary.type === "hourly" ? wageTypeSet === null || wageTypeSet === void 0 ? void 0 : wageTypeSet.workHourlyDays : wageTypeSet === null || wageTypeSet === void 0 ? void 0 : wageTypeSet.workMonthlyDays,
                    costCenter,
                    exportOnly: true,
                });
            }
            // commission
            if (salary.commission && timeEntry.revenue) {
                addItem({
                    type: PayrollItemType.Commission,
                    description: positionLabel,
                    icon: "badge-dollar",
                    unit: "euros",
                    wagePerUnit: salary.commission,
                    amount: timeEntry.revenue,
                    result: (timeEntry.revenue * salary.commission) / 100,
                    wageType: wageTypeSet === null || wageTypeSet === void 0 ? void 0 : wageTypeSet.commission,
                    costCenter,
                });
            }
            // meals
            if (result.meals.breakfast && company.settings.mealValueBreakfast) {
                addItem({
                    type: PayrollItemType.Meals,
                    description: `Frühstück (${positionLabel})`,
                    icon: "utensils",
                    unit: "pieces",
                    wagePerUnit: result.meals.breakfast.value,
                    amount: result.meals.breakfast.count,
                    result: result.meals.breakfast.costs,
                    wageType: wageTypeSet === null || wageTypeSet === void 0 ? void 0 : wageTypeSet.mealsBreakfast,
                    costCenter,
                });
            }
            if (result.meals.lunch && company.settings.mealValueLunch) {
                addItem({
                    type: PayrollItemType.Meals,
                    description: `Mittagessen (${positionLabel})`,
                    icon: "utensils",
                    unit: "pieces",
                    wagePerUnit: result.meals.lunch.value,
                    amount: result.meals.lunch.count,
                    result: result.meals.lunch.costs,
                    wageType: wageTypeSet === null || wageTypeSet === void 0 ? void 0 : wageTypeSet.mealsLunch,
                    costCenter,
                });
            }
            if (result.meals.dinner && company.settings.mealValueDinner) {
                addItem({
                    type: PayrollItemType.Meals,
                    description: `Abendessen (${positionLabel})`,
                    icon: "utensils",
                    unit: "pieces",
                    wagePerUnit: result.meals.dinner.value,
                    amount: result.meals.dinner.count,
                    result: result.meals.dinner.costs,
                    wageType: wageTypeSet === null || wageTypeSet === void 0 ? void 0 : wageTypeSet.mealsDinner,
                    costCenter,
                });
            }
            const totalMealsValue = (0, openapi_1.sum)(((_f = result.meals.breakfast) === null || _f === void 0 ? void 0 : _f.costs) || 0, ((_g = result.meals.lunch) === null || _g === void 0 ? void 0 : _g.costs) || 0, ((_h = result.meals.dinner) === null || _h === void 0 ? void 0 : _h.costs) || 0);
            if (totalMealsValue > 0) {
                addItem({
                    type: PayrollItemType.Benefit, // PayrollItemType.Meals is only for the number of meals, PayrollItemType.Benefit is used for the euro value here
                    description: `Mitarbeiteressen (${positionLabel})`,
                    icon: "utensils",
                    unit: "euros",
                    amount: totalMealsValue,
                    result: totalMealsValue,
                    wageType: wageTypeSet === null || wageTypeSet === void 0 ? void 0 : wageTypeSet.mealsValue,
                    costCenter,
                    exportOnly: true,
                });
            }
        }
        else if (model_1.timeEntryAbsenceTypes.some((absenceType) => absenceType === timeEntry.type)) {
            const type = timeEntry.type;
            // Absence hours
            addItem({
                type: PayrollItemType.Absence,
                description: localized_1.Localized[company.country].timeEntryTypeLabel(type),
                icon: localized_1.Localized[company.country].timeEntryTypeIcon(type),
                unit: "hours",
                amount: (0, openapi_1.round)(result.base.duration, 2),
                wagePerUnit: (salary === null || salary === void 0 ? void 0 : salary.type) === "hourly" ? result.base.hourlyRate : undefined,
                result: (salary === null || salary === void 0 ? void 0 : salary.type) === "hourly" ? result.base.wages : undefined,
                wageType: {
                    [model_1.TimeEntryType.Vacation]: absenceWageTypes === null || absenceWageTypes === void 0 ? void 0 : absenceWageTypes.vacation,
                    [model_1.TimeEntryType.Sick]: absenceWageTypes === null || absenceWageTypes === void 0 ? void 0 : absenceWageTypes.sick,
                    [model_1.TimeEntryType.ChildSick]: absenceWageTypes === null || absenceWageTypes === void 0 ? void 0 : absenceWageTypes.childSick,
                    [model_1.TimeEntryType.SickInKUG]: absenceWageTypes === null || absenceWageTypes === void 0 ? void 0 : absenceWageTypes.sickInKUG,
                    [model_1.TimeEntryType.CompDay]: absenceWageTypes === null || absenceWageTypes === void 0 ? void 0 : absenceWageTypes.compDay,
                }[type],
                costCenter,
            });
            // absence days
            if (result.days) {
                addItem({
                    type: type === model_1.TimeEntryType.Vacation ? PayrollItemType.VacationDays : PayrollItemType.SickDays,
                    description: `${localized_1.Localized[company.country].timeEntryTypeLabel(type)} (Tage)`,
                    icon: localized_1.Localized[company.country].timeEntryTypeIcon(type),
                    unit: "days",
                    amount: result.days,
                    wageType: {
                        [model_1.TimeEntryType.Vacation]: absenceDaysWageTypes === null || absenceDaysWageTypes === void 0 ? void 0 : absenceDaysWageTypes.vacation,
                        [model_1.TimeEntryType.Sick]: absenceDaysWageTypes === null || absenceDaysWageTypes === void 0 ? void 0 : absenceDaysWageTypes.sick,
                        [model_1.TimeEntryType.ChildSick]: absenceDaysWageTypes === null || absenceDaysWageTypes === void 0 ? void 0 : absenceDaysWageTypes.childSick,
                        [model_1.TimeEntryType.SickInKUG]: absenceDaysWageTypes === null || absenceDaysWageTypes === void 0 ? void 0 : absenceDaysWageTypes.sickInKUG,
                        [model_1.TimeEntryType.CompDay]: absenceDaysWageTypes === null || absenceDaysWageTypes === void 0 ? void 0 : absenceDaysWageTypes.compDay,
                    }[type],
                    costCenter,
                    exportOnly: true,
                });
            }
        }
        else if (timeEntry.type === model_1.TimeEntryType.HourAdjustment && result.base.duration) {
            if (!timeEntry.paid) {
                continue;
            }
            // Add Hour Adjustments
            addItem({
                type: PayrollItemType.HourAdjustment,
                description: timeEntry.comment || localized_1.Localized[company.country].timeEntryTypeLabel(timeEntry.type),
                icon: "plus-minus",
                unit: "hours",
                wagePerUnit: result.base.hourlyRate,
                amount: (0, openapi_1.round)(Math.abs(result.base.duration), 2),
                result: Math.abs(result.base.wages),
                wageType: timeEntry.wageType || undefined,
                costCenter,
            });
        }
        else if (timeEntry.type === model_1.TimeEntryType.VacationAdjustment && result.base.duration) {
            // Add Vacation Adjustments
            addItem({
                type: PayrollItemType.VacationAdjustment,
                description: `${timeEntry.comment || localized_1.Localized[company.country].timeEntryTypeLabel(timeEntry.type)} (${result.days} T)`,
                icon: "plus-minus",
                unit: "hours",
                wagePerUnit: result.base.hourlyRate,
                amount: (0, openapi_1.round)(result.base.duration, 2),
                result: result.base.wages,
                wageType: timeEntry.wageType || undefined,
                costCenter,
            });
        }
        // Add bonuses
        for (const bonus of result.bonuses) {
            const type = company.bonusTypes.find((t) => t.id === bonus.type.id);
            if (!type || !bonus.duration) {
                continue;
            }
            const wageTypes = wageTypeSet === null || wageTypeSet === void 0 ? void 0 : wageTypeSet.bonuses.find((b) => b.bonusTypeId === bonus.type.id);
            const activeContract = contracts.find((c) => c.isActiveOn(timeEntry.date));
            addItem({
                type: bonus.taxFree ? PayrollItemType.BonusTaxFree : PayrollItemType.BonusTaxed,
                description: type.name,
                icon: "badge-percent",
                unit: type.compType === model_1.BonusCompType.FixedAmount ? "days" : "hours",
                amount: type.compType === model_1.BonusCompType.FixedAmount ? 1 : (0, openapi_1.round)(bonus.duration, 2),
                wagePerUnit: type.compType === model_1.BonusCompType.FixedAmount ? bonus.wages : bonus.hourlyRate,
                factor: bonus.percent,
                result: (activeContract === null || activeContract === void 0 ? void 0 : activeContract.enableSFNLedger) && activeContract.sfnAdvance && bonus.taxFree
                    ? undefined
                    : bonus.wages,
                wageType: wageTypes && (bonus.taxFree ? wageTypes.taxFree : wageTypes.taxed),
                costCenter,
            });
        }
    }
    // Add Pay Advance
    for (const entry of advances) {
        addItem({
            type: PayrollItemType.PayAdvance,
            description: `Vorschuss vom ${(0, utils_1.formatDate)(entry.date)} (${entry.cashbook ? "bar" : "Überweisung"})`,
            icon: "hand-holding-dollar",
            unit: "euros",
            amount: -1 * entry.amount,
            result: entry.amount,
            wageType: defaultWageTypeSet === null || defaultWageTypeSet === void 0 ? void 0 : defaultWageTypeSet.payAdvance,
            costCenter: defaultCostCenter,
        });
    }
    return {
        employee,
        from,
        to,
        items: items.sort((a, b) => order.indexOf(a.type) - order.indexOf(b.type)),
        totalPay: items.filter((item) => !item.exportOnly).reduce((total, item) => total + (item.result || 0), 0),
    };
}
/**
 * Get hourly rate in euro cents for a given contract and position
 */
function getHourlyRate(company, contract, positionId) {
    const weekFactor = company.settings.weekFactor || 4.35;
    const comp = (0, contract_util_1.getSalaryOrDefault)(positionId, contract.salaries);
    if (!comp) {
        return 0;
    }
    if (comp.type === "hourly") {
        return (0, openapi_1.getRate)(comp.amount, 1);
    }
    const salaryTotal = (0, openapi_1.sum)(comp.amount, ...contract.benefits
        .filter((b) => { var _a, _b; return (_b = (_a = company.benefitTypes) === null || _a === void 0 ? void 0 : _a.find((t) => t.id === b.typeId)) === null || _b === void 0 ? void 0 : _b.includeInBonusPayments; })
        .map((benefit) => benefit.amount));
    const averageHoursPerMonth = (0, openapi_1.multiplyWithFactor)(contract.nominalWeeklyHours, weekFactor);
    return (0, openapi_1.round)((0, openapi_1.getRate)(salaryTotal, averageHoursPerMonth), 2);
}
function getAncillaryCostFactor(type) {
    const ANCILLARY_COST_FACTORS = {
        [model_1.EmploymentType.ShortTerm]: [0, 0],
        [model_1.EmploymentType.Independent]: [0, 0],
        [model_1.EmploymentType.Marginal]: [0.348, 0],
        [model_1.EmploymentType.Regular]: [0.19825, 0.19825],
        [model_1.EmploymentType.Trainee]: [0.19825, 0.19825],
        [model_1.EmploymentType.MidiJob]: [0.19825, 0.19825],
        [model_1.EmploymentType.WorkingStudent]: [0.0935, 0.0935],
        [model_1.EmploymentType.DualStudent]: [0.19825, 0.19825],
        [model_1.EmploymentType.Intern]: [0, 0],
    };
    return ANCILLARY_COST_FACTORS[type] || [0, 0];
}
/*
    Definition: A Payment is either:
    - a Salary
    - a Benefit
    - a SFN Advance

    A Contract is basically a DateRange as is the range we are looking at.
    Since payment calculations are always done on a monthly payroll period basis, we need to split the supplied range into month-long payroll periods (intervals).
    For each interval, we calculate the number of days the contract is active and then calculate the prorated payment for that period.
    To calculate the prorated payment correctly we need to consider the following cases:

    1. contract is active whole period --> entire payment
    |----- period -----|
    |------ con. -----|

    2. contract begins and ends within period --> payment for days with contract
    |----- period -----|
       |-- con. --|

    3. contract begins in period --> payment for days with contract until end of period
    |----- period -----|
           |-- con. ---------|
           
    4. contract ends in period --> payment for days with contract starting at beginning of period
          |----- period -----|
    |------- con. ---|

*/
function getProratedPaymentForRange(contract, range, payment, startDayOfPayrollPeriod = 1) {
    var _a;
    const baseValue = typeof payment === "number" ? payment : payment.amount;
    const effectiveRange = (0, utils_1.intersectRanges)(range, {
        from: contract.start,
        to: (_a = contract.end) !== null && _a !== void 0 ? _a : (0, utils_1.toDateString)(new Date(Number.MAX_SAFE_INTEGER)),
    });
    if (!effectiveRange) {
        console.log("No effective range - returning empty array");
        return [];
    }
    const payrollPeriods = transformToPayrollIntervals(effectiveRange, startDayOfPayrollPeriod);
    return payrollPeriods.map((period) => {
        const fullPayrollPeriod = getPayrollPeriod(period.from, startDayOfPayrollPeriod);
        const result = calculateProratedPayment(period, fullPayrollPeriod, baseValue);
        return result;
    });
}
function calculateProratedPayment(activeRange, baseRange, baseValue) {
    const effectiveRange = (0, utils_1.intersectRanges)(baseRange, activeRange);
    if (!effectiveRange) {
        console.log("Invalid date range - earliest end is before latest start!!!");
        return {
            from: baseRange.from,
            to: baseRange.to,
            amount: 0,
        };
    }
    // Calculate the number of days in the month and the number of days the contract is active
    const totalDaysInRange = (0, utils_1.dateSub)(baseRange.from, baseRange.to);
    const activeDays = (0, utils_1.dateSub)(effectiveRange.from, effectiveRange.to);
    // Calculate the prorated payment
    const proratedPayment = (0, openapi_1.round)((activeDays / totalDaysInRange) * baseValue, 2);
    return {
        from: effectiveRange.from,
        to: effectiveRange.to,
        amount: proratedPayment,
    };
}
function transformToPayrollIntervals(dateRange, startDayOfPayrollInterval = 1) {
    const { from, to } = dateRange;
    const intervals = [];
    let currentStart = from;
    while (currentStart < to) {
        // Get first day of next payroll periods
        const endOfPayrollPeriod = getPayrollPeriod(currentStart, startDayOfPayrollInterval).to;
        // Determine the end date of the current interval
        const currentEndDate = endOfPayrollPeriod < to ? endOfPayrollPeriod : to;
        // Push the interval into the array
        intervals.push({
            from: currentStart,
            to: currentEndDate,
        });
        // Move to the start of the next payment period
        currentStart = currentEndDate;
    }
    return intervals;
}
/** finds the payroll period that contains the given date */
function getPayrollPeriod(date, startOfPayrollPeriod) {
    const [year, month] = date.split("-");
    let periodStart = `${year}-${month}-${startOfPayrollPeriod.toString().padStart(2, "0")}`;
    // If the date is before the start day of the period, we need to use the previous month
    if (date < periodStart) {
        periodStart = (0, utils_1.dateAdd)(periodStart, { months: -1 });
    }
    const periodEnd = (0, utils_1.dateAdd)(periodStart, { months: 1 });
    return {
        from: periodStart,
        to: periodEnd,
    };
}
//# sourceMappingURL=payroll.js.map